/**
 * BodyContent — routes the visible body surface from the current
 * `(currentApp, currentPage)` tuple.
 *
 * Surfaces wired:
 *   tru-clara-app / __clara-ai-chat__ → RemotePage (Angular ClaraAI Native Federation)
 *   kci-workspaces / __home__         → RemoteWorkspaceDashboard (kci-workspace MFE)
 *   anything else                    → page-config fallback (title + desc)
 *
 * EngagementLanding + PortfolioGrid land in a later phase once the
 * body-card-grid CSS is fully ported and engagement-scoped data flows
 * from the store.
 */

import React, { useLayoutEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigation } from '../../stores/useAppStore';
import { useT } from '../../i18n/I18nProvider';
import { getPageConfig } from '../../services/appRegistry';
import { PulseDashboard } from '../dashboards/PulseDashboard';
import { RemoteWorkspaceDashboard } from '../../components/RemoteWorkspaceDashboard/RemoteWorkspaceDashboard';
import { RemoteReviewBalanceDashboard } from '../../components/RemoteWorkspaceDashboard/RemoteReviewBalanceDashboard';
import { RemoteDataDrivenInsightsDashboard, RemoteDataDrivenAssessmentDashboard, RemoteViewReportDashboard } from '../../components/RemoteWorkspaceDashboard/RemoteWorkspaceTabPages';
import { RemoteExecutionStreamDashboard } from '../../components/RemoteWorkspaceDashboard/RemoteExecutionStreamDashboard';
import { isMaintenanceModeEnabled } from '../../services/dockService';
import { CONFIG } from '../../constants';
import { RemotePage } from '../../components/RemotePage/RemotePage';
import { FileViewerTab } from '../workspace/file-viewer-tab';
import { useFileViewerContext } from '../../contexts/FileViewerContext';
import { dispatchChatSessionLoad, dispatchActivityDashboardLoad } from '../../utils';
import { useTabState } from '../../stores/useTabStore';
import { AGENT_DASHBOARD_TAB_ID } from '../../hooks/useNavTabs';


const TAB_ROUTE_ID_STORAGE_KEY = 'kci-core.navTabs.routeToId.v1';

function getOpenObjectiveTabIdsFromStorage(): Set<string> {
  if (typeof window === 'undefined') {
    return new Set();
  }

  try {
    const raw = window.localStorage.getItem(TAB_ROUTE_ID_STORAGE_KEY);
    if (!raw) {
      return new Set();
    }

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    // Keys are "objective:<objectiveToken>" -> tabId; the open set is keyed
    // by objectiveToken (the workspace container token's subPage), not by
    // tabId, so it must be read off the route key, not the stored value.
    const ids = Object.entries(parsed)
      .filter(([routeKey, tabId]) => routeKey.startsWith('objective:') && typeof tabId === 'string')
      .map(([routeKey]) => routeKey.slice('objective:'.length));

    return new Set(ids);
  } catch {
    return new Set();
  }
}

function extractSubPageFromWorkspaceToken(token: string): string | null {
  // Token shape: workspace:<app>:<page>:<subPage>
  const parts = token.split(':');
  if (parts.length < 4) {
    return null;
  }
  return parts.slice(3).join(':');
}

function WorkspaceRemoteContainerStack({ activeToken }: { activeToken: string | null }) {
  const { workspaceContainerTokens, workspaceRemoteHosts, workspaceConfigs } = useNavigation().navigation;

  const homeTokens = workspaceContainerTokens.filter((token) => {
    const parts = token.split(':');
    return parts[0] === 'workspace' && parts[2] === CONFIG.FIXED_TAB_HOME;
  });
  const tokens = activeToken && !homeTokens.includes(activeToken)
    ? [activeToken, ...homeTokens]
    : homeTokens;

  const openObjectiveTabIds = getOpenObjectiveTabIdsFromStorage();
  const mountedTokens = tokens.filter((token) => {
    if (token === activeToken) {
      return true;
    }

    const subPage = extractSubPageFromWorkspaceToken(token);

    // Non-objective containers (no subPage, or the default __home__ landing
    // with nothing selected) remain mounted for keep-alive behavior. Every
    // other subPage under __home__ is an objectiveToken — there is no
    // "objective-" prefix in practice (see handleWorkspaceSecondaryTabOpen),
    // so any non-default subPage here means "objective tab".
    if (!subPage || subPage === 'default') {
      return true;
    }

    // Objective tab instances are keep-alive only while their owning tab exists.
    return openObjectiveTabIds.has(subPage);
  });

  return (
    <>
      {mountedTokens.map((token) => {
        const isActive = token === activeToken;
        return (
          <div
            key={`remote:${token}`}
            style={{ display: isActive ? 'block' : 'none', width: '100%' }}
            aria-hidden={!isActive}
          >
            <RemoteWorkspaceDashboard
              isActive={isActive}
              regionalUiHost={workspaceRemoteHosts?.[token]}
              Config={workspaceConfigs[token]}
            />
          </div>
        );
      })}
    </>
  );
}

// Keep-alive stack for Data-Driven Insights tabs — preserves UI state across tab switches.
function DataDrivenInsightsContainerStack({ activeToken }: { activeToken: string }) {
  const { workspaceContainerTokens } = useNavigation().navigation;

  const ddiTokens = workspaceContainerTokens.filter((token) => {
    const parts = token.split(':');
    return parts[0] === 'workspace' && parts[2] === '__data-driven-insights__';
  });
  const tokens = ddiTokens.includes(activeToken) ? ddiTokens : [activeToken, ...ddiTokens];

  return (
    <>
      {tokens.map((token) => {
        const isActive = token === activeToken;
        const storageKey = extractSubPageFromWorkspaceToken(token) ?? '';
        return (
          <div
            key={`ddi:${token}`}
            style={{ display: isActive ? 'block' : 'none', width: '100%', height: '100%' }}
            aria-hidden={!isActive}
          >
            <RemoteDataDrivenInsightsDashboard storageKey={storageKey} />
          </div>
        );
      })}
    </>
  );
}

// Keep-alive stack for Data-Driven Assessment tabs — preserves UI state across tab switches.
function DataDrivenAssessmentContainerStack({ activeToken }: { activeToken: string }) {
  const { workspaceContainerTokens } = useNavigation().navigation;

  const ddaTokens = workspaceContainerTokens.filter((token) => {
    const parts = token.split(':');
    return parts[0] === 'workspace' && parts[2] === '__data-driven-assessment__';
  });
  const tokens = ddaTokens.includes(activeToken) ? ddaTokens : [activeToken, ...ddaTokens];

  return (
    <>
      {tokens.map((token) => {
        const isActive = token === activeToken;
        const storageKey = extractSubPageFromWorkspaceToken(token) ?? '';
        return (
          <div
            key={`dda:${token}`}
            style={{ display: isActive ? 'block' : 'none', width: '100%', height: '100%' }}
            aria-hidden={!isActive}
          >
            <RemoteDataDrivenAssessmentDashboard storageKey={storageKey} />
          </div>
        );
      })}
    </>
  );
}

const normalizeRemotePath = (rawPath: string) => {
  const value = String(rawPath ?? '').trim();
  if (!value) return '';

  // Handle absolute URLs emitted by a remote.
  try {
    const parsed = new URL(value);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return value;
  }
};

const mapAgentHostToRemotePath = (hostPath: string) => {
  const normalizedHostPath = normalizeRemotePath(hostPath);

  if (
    normalizedHostPath === '/agents' ||
    normalizedHostPath.startsWith('/agents?') ||
    normalizedHostPath === '/agents/action-tray' ||
    normalizedHostPath.startsWith('/agents/action-tray') ||
    normalizedHostPath === '/dashboard' ||
    normalizedHostPath.startsWith('/dashboard') ||
    normalizedHostPath === '/agents/agent-dashboard' ||
    normalizedHostPath.startsWith('/agents/agent-dashboard')
  ) {
    return '/dashboard';
  }

  const agentPathMatch = normalizedHostPath.match(/^\/agents\/([a-z0-9-]+)/);
  if (agentPathMatch?.[1]) {
    return `/${agentPathMatch[1]}`;
  }

  return normalizedHostPath;
};

const mapAgentRemoteToHostPath = (remotePath: string) => {
  const normalizedRemotePath = normalizeRemotePath(remotePath);

  if (
    !normalizedRemotePath ||
    normalizedRemotePath === '/' ||
    normalizedRemotePath.startsWith('/dashboard') ||
    normalizedRemotePath.startsWith('/agent-dashboard')
  ) {
    return '/dashboard';
  }

  if (normalizedRemotePath.startsWith('/agents')) {
    return normalizedRemotePath;
  }

  return `/agents${normalizedRemotePath.startsWith('/') ? '' : '/'}${normalizedRemotePath}`;
};

export const BodyContent = React.memo(function BodyContent() {
  const navigation = useNavigation();
  const t = useT();
  const { getFile } = useFileViewerContext();
  const claraRemoteEntryUrl = `${import.meta.env.VITE_claraAppUrl ?? 'http://localhost:4100'}/remoteEntry.json`;

  const { search } = useLocation();
  const queryAiIntegrationId = useMemo(() => {
    const value = new URLSearchParams(search).get('aiintegrationid')?.trim();
    return value && value !== 'undefined' && value !== 'null' ? value : null;
  }, [search]);

  // Shared with the ClaraAI remote, which reads 'aiintegrationid' from sessionStorage on init.
  useLayoutEffect(() => {
    if (queryAiIntegrationId) {
      sessionStorage.setItem('aiintegrationid', queryAiIntegrationId);
    }
  }, [queryAiIntegrationId]);

  const { activeTabId } = useTabState();
  const { currentApp, currentPage, currentSubPage } = navigation.navigation;
  const workspaceContainerToken = useMemo(
    () => `workspace:${currentApp}:${currentPage}:${currentSubPage ?? 'default'}`,
    [currentApp, currentPage, currentSubPage]
  );
  const isClaraChatActive =
    currentApp === CONFIG.CLARA_APP && currentPage === CONFIG.FIXED_TAB_CLARA_AI_CHAT;
  // Dashboard tab activation never changes currentApp/URL (see useNavTabs.ts),
  // so its visibility is driven by the active tab id, not the nav tuple.
  const isAgentDashboardTabActive = activeTabId === AGENT_DASHBOARD_TAB_ID;
  const canKeepClaraChatMounted =
    currentApp !== CONFIG.AGENTS && currentApp !== 'angular-remote';
  const isChatContainerVisible = isClaraChatActive || isAgentDashboardTabActive;
  const isWorkspaceHomeActive =
    currentApp === CONFIG.WORKSPACE &&
    currentPage === CONFIG.FIXED_TAB_HOME &&
    !isAgentDashboardTabActive;
  const hasWorkspaceHomeContainer = navigation.navigation.workspaceContainerTokens.some((token) => {
    const parts = token.split(':');
    return parts[0] === 'workspace' && parts[1] === CONFIG.WORKSPACE && parts[2] === CONFIG.FIXED_TAB_HOME;
  });

  const renderTransientContent = () => {

  // ── Angular Micro-frontend (Native Federation remote) ────────────────────
  // Note: Legacy angular-remote app routing kept for backward compatibility.
  // Use the Clara AI Chat route (tru-clara-app / __clara-ai-chat__) instead.
  if (currentApp === 'angular-remote') {
    return (
      <div className="body-content">
        <RemotePage
          remoteName="Angular Remote"
          remoteEntryUrl={claraRemoteEntryUrl}
          remoteHashRouting="always"
        />
      </div>
    );
  }

  if (currentApp === CONFIG.CLARA_APP) {
    // File viewer tab: page ID is "file-view:<assetId>"
    if (currentPage.startsWith('file-view:')) {
      const asset = getFile(currentPage);
      if (asset) {
        return (
          <div className="body-content body-content--file-viewer">
            <FileViewerTab asset={asset} />
          </div>
        );
      }
    }

    if (currentPage === CONFIG.FIXED_TAB_CLARA_AI_CHAT) {
      return null;
    }
  }

  if (currentApp === CONFIG.WORKSPACE) {
    // File viewer tab: page ID is "file-view:<assetId>"
    if (currentPage.startsWith('file-view:')) {
      const asset = getFile(currentPage);
      if (asset) {
        return (
          <div className="body-content body-content--file-viewer">
            <FileViewerTab asset={asset} />
          </div>
        );
      }
    }

    if (currentPage === CONFIG.FIXED_TAB_HOME) {
      return null;
    }

    if (currentPage === '__review-balance__') {
      return (
        <div className="body-content body-content--fullpage">
          <RemoteReviewBalanceDashboard storageKey={currentSubPage ?? ''} />
        </div>
      );
    }

    if (currentPage === '__data-driven-insights__') {
      return (
        <div className="body-content body-content--fullpage">
          <DataDrivenInsightsContainerStack activeToken={workspaceContainerToken} />
        </div>
      );
    }

    if (currentPage === '__data-driven-assessment__') {
      return (
        <div className="body-content body-content--fullpage body-content--workspace-left-nav">
          <DataDrivenAssessmentContainerStack activeToken={workspaceContainerToken} />
        </div>
      );
    }

    if (currentPage === '__view-report__') {
      return (
        <div className="body-content body-content--fullpage">
          <RemoteViewReportDashboard storageKey={currentSubPage ?? ''} />
        </div>
      );
    }

    if (currentPage === '__execution-stream__' && isMaintenanceModeEnabled()) {
      return (
        <div className="body-content body-content--fullpage">
          <RemoteExecutionStreamDashboard />
        </div>
      );
    }
   

   
  
  }

  // Agents — load Angular 21 remote on port 4200.
  if (currentApp === CONFIG.AGENTS) {
    if (
      currentPage === CONFIG.FIXED_TAB_HOME ||
      currentPage === 'agent-dashboard' ||
      currentPage === CONFIG.PAGE_ACTION_TRAY
    ) {
      return (
        <div className="body-content">
          {/* <AgentsDashboard /> */}
          <RemotePage
            remoteName="Agents Remote"
            remoteEntryUrl={claraRemoteEntryUrl}
            remoteRuntimeName="ai-chat"
            remoteElementTag="clara-ai-chat-element"
            mapHostToRemotePath={mapAgentHostToRemotePath}
            mapRemoteToHostPath={mapAgentRemoteToHostPath}
            remoteHashRouting="never"
            preserveHostPath="/dashboard"
          />
        </div>
      );
    } 
   
    return (
      <div className="body-content">
        <RemotePage
          remoteName="angularRemote"
          remoteEntryUrl={claraRemoteEntryUrl}
          mapHostToRemotePath={mapAgentHostToRemotePath}
          mapRemoteToHostPath={mapAgentRemoteToHostPath}
          remoteHashRouting="never"
          preserveHostPath="/dashboard"
        />
      </div>
    );
  }

  // Pulse (tru-pulse) — show PulseDashboard at the app root.
  if (currentApp === 'tru-pulse') {
    return (
      <div className="body-content">
        <PulseDashboard />
      </div>
    );
  }



  const pageConfig = getPageConfig(currentApp, currentPage);

  return (
    <div className="body-content">
      <div className="body-content__page" style={{ padding: '40px' }}>
        <h1 className="body-content__title">{pageConfig?.title ?? t('body.fallbackTitle')}</h1>
        <p className="body-content__desc">{pageConfig?.desc ?? ''}</p>
      </div>
    </div>
  );
  };

  return (
    <>
      {canKeepClaraChatMounted && (
        <div
          className="body-content"
          style={{ display: isChatContainerVisible ? 'flex' : 'none' }}
          aria-hidden={!isChatContainerVisible}
        >
          <RemotePage
            remoteName="ClaraAI Chat"
            remoteEntryUrl={claraRemoteEntryUrl}
            remoteRuntimeName="ai-chat"
            remoteElementTag="clara-ai-chat-element"
            mapHostToRemotePath={() => '/chat'}
            mapRemoteToHostPath={() => ''}
            remoteHashRouting="always"
            preserveHostPath
            onReady={() => {
              if (isAgentDashboardTabActive) {
                dispatchActivityDashboardLoad();
                return;
              }

              if (!currentSubPage) {
                return;
              }

              console.info('[KCI-ChatSession][Core] Replaying session after remote mount', {
                sessionId: currentSubPage,
              });
              dispatchChatSessionLoad(currentSubPage);
            }}
          />
        </div>
      )}

      {hasWorkspaceHomeContainer && (
        <div
          className="body-content"
          style={{ display: isWorkspaceHomeActive ? 'flex' : 'none' }}
          aria-hidden={!isWorkspaceHomeActive}
        >
          <WorkspaceRemoteContainerStack
            activeToken={isWorkspaceHomeActive ? workspaceContainerToken : null}
          />
        </div>
      )}

      {!isClaraChatActive && !isWorkspaceHomeActive && !isAgentDashboardTabActive && renderTransientContent()}
    </>
  );
});

export default BodyContent;